import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../utils/toast';

// Mensagens diretas aluno ↔ tutor do curso (tabela mensagens_tutor, mig 026).
// Conversa = (curso, aluno). Aluno vê só as dele (RLS); a equipe vê todas.

const fmtHora = (iso) => {
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  return mesmoDia
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function MensagensTutor({ isGestor, usuarioAtual, cursos = [], usuarios = [] }) {
  const meuId = usuarioAtual?.id;
  const [msgs, setMsgs] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sel, setSel] = useState(null); // { cursoId, alunoId }
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [nova, setNova] = useState(null); // gestor iniciando conversa: { cursoId, alunoId }
  const fimRef = useRef(null);

  const carregar = async () => {
    const { data, error } = await supabase.from('mensagens_tutor').select('*').order('criado_em', { ascending: true }).limit(2000);
    if (!error) setMsgs(data || []);
    setCarregando(false);
  };

  useEffect(() => {
    if (!meuId) return;
    carregar();
    const canal = supabase.channel(`mensagens-tutor-${meuId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'mensagens_tutor' }, () => carregar())
      .subscribe();
    return () => { supabase.removeChannel(canal); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meuId]);

  const nomeCurso = (id) => cursos.find(c => String(c.id) === String(id))?.nome || 'Curso';
  const nomeUsuario = (id) => usuarios.find(u => String(u.id) === String(id))?.nome || 'Aluno';

  // Conversas: aluno → uma por curso dele; equipe → todas que existem.
  const conversas = useMemo(() => {
    const mapa = new Map();
    if (!isGestor) cursos.forEach(c => mapa.set(`${c.id}|${meuId}`, { cursoId: String(c.id), alunoId: meuId, msgs: [] }));
    msgs.forEach(m => {
      const k = `${m.curso_id}|${m.aluno_id}`;
      if (!mapa.has(k)) mapa.set(k, { cursoId: m.curso_id, alunoId: m.aluno_id, msgs: [] });
      mapa.get(k).msgs.push(m);
    });
    return [...mapa.values()]
      .map(c => ({ ...c, ultima: c.msgs[c.msgs.length - 1], naoLidas: c.msgs.filter(m => !m.lida_em && m.autor_id !== meuId && (isGestor ? m.autor_id === m.aluno_id : true)).length }))
      .sort((a, b) => String(b.ultima?.criado_em || '').localeCompare(String(a.ultima?.criado_em || '')));
  }, [msgs, cursos, isGestor, meuId]);

  const atual = sel && (conversas.find(c => c.cursoId === sel.cursoId && c.alunoId === sel.alunoId) || { ...sel, msgs: [] });

  // Abre a primeira conversa por padrão.
  useEffect(() => {
    if (!sel && conversas.length) setSel({ cursoId: conversas[0].cursoId, alunoId: conversas[0].alunoId });
  }, [conversas, sel]);

  // Marca como lidas as mensagens recebidas na conversa aberta.
  useEffect(() => {
    if (!atual) return;
    const ids = atual.msgs.filter(m => !m.lida_em && m.autor_id !== meuId).map(m => m.id);
    if (ids.length) supabase.from('mensagens_tutor').update({ lida_em: new Date().toISOString() }).in('id', ids).then(() => {});
    fimRef.current?.scrollIntoView({ block: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atual?.cursoId, atual?.alunoId, atual?.msgs.length]);

  const enviar = async () => {
    const t = texto.trim();
    if (!t || !atual) return;
    setEnviando(true);
    const { data: nova, error } = await supabase.from('mensagens_tutor').insert({
      curso_id: String(atual.cursoId), aluno_id: atual.alunoId, autor_id: meuId,
      autor_nome: usuarioAtual?.nome || usuarioAtual?.email || '', texto: t,
    }).select('id').single();
    setEnviando(false);
    if (error) { toast.erro('Não foi possível enviar a mensagem. Tente novamente.'); return; }
    setTexto('');
    carregar();
    // Aviso por e-mail ao outro lado (api/notificar-mensagem). Não bloqueia o chat.
    supabase.auth.getSession().then(({ data }) => {
      const token = data?.session?.access_token;
      if (!token || !nova?.id) return;
      fetch('/api/notificar-mensagem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: nova.id }),
      }).catch(() => {});
    });
  };

  const alunos = usuarios.filter(u => u.papel === 'aluno').sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
  const btn = { border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

  if (carregando) return <div style={{ padding: '1rem', color: '#5f5874' }}>Carregando mensagens...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, color: '#1c1530', fontSize: 15 }}>{isGestor ? '💬 Mensagens dos alunos' : '💬 Fale com o tutor'}</div>
        {isGestor && !nova && <button style={{ ...btn, background: '#7c3aed', color: '#fff' }} onClick={() => setNova({ cursoId: '', alunoId: '' })}>+ Nova conversa</button>}
      </div>
      {!isGestor && <div style={{ fontSize: 13, color: '#5f5874' }}>Envie sua dúvida sobre o curso. O tutor responde por aqui.</div>}

      {isGestor && nova && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: 10, border: '1px solid #e5e0ef', borderRadius: 10 }}>
          <select value={nova.alunoId} onChange={e => setNova({ ...nova, alunoId: e.target.value })} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e0ef' }}>
            <option value="">Aluno...</option>
            {alunos.map(a => <option key={a.id} value={a.id}>{a.nome}{a.empresa ? ` (${a.empresa})` : ''}</option>)}
          </select>
          <select value={nova.cursoId} onChange={e => setNova({ ...nova, cursoId: e.target.value })} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e0ef' }}>
            <option value="">Curso...</option>
            {[...cursos].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <button style={{ ...btn, background: '#7c3aed', color: '#fff' }} disabled={!nova.alunoId || !nova.cursoId}
            onClick={() => { setSel({ cursoId: String(nova.cursoId), alunoId: nova.alunoId }); setNova(null); }}>Abrir</button>
          <button style={{ ...btn, background: '#fff', border: '1px solid #e5e0ef' }} onClick={() => setNova(null)}>Cancelar</button>
        </div>
      )}

      {conversas.length === 0 && !atual ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#5f5874', border: '1px dashed #e5e0ef', borderRadius: 12 }}>
          {isGestor ? 'Nenhuma mensagem recebida ainda.' : 'Você ainda não está matriculado em nenhum curso.'}
        </div>
      ) : (
        <div className="mensagens-tutor" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)', gap: 12, minHeight: 420 }}>
          <div style={{ border: '1px solid #e5e0ef', borderRadius: 12, overflowY: 'auto', maxHeight: 560 }}>
            {conversas.map(c => {
              const ativa = atual && c.cursoId === atual.cursoId && c.alunoId === atual.alunoId;
              return (
                <button key={`${c.cursoId}|${c.alunoId}`} onClick={() => setSel({ cursoId: c.cursoId, alunoId: c.alunoId })}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f3f0f9', background: ativa ? '#ede6fb' : '#fff', cursor: 'pointer', minHeight: 0 }}>
                  <div style={{ display: 'flex', width: '100%', gap: 6, alignItems: 'center' }}>
                    <b style={{ fontSize: 13, color: '#1c1530', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isGestor ? nomeUsuario(c.alunoId) : nomeCurso(c.cursoId)}
                    </b>
                    {c.naoLidas > 0 && <span style={{ background: '#7c3aed', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{c.naoLidas}</span>}
                  </div>
                  {isGestor && <span style={{ fontSize: 11, color: '#5b21b6' }}>{nomeCurso(c.cursoId)}</span>}
                  <span style={{ fontSize: 12, color: '#64748b', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.ultima ? c.ultima.texto : 'Nenhuma mensagem ainda'}
                  </span>
                </button>
              );
            })}
          </div>

          {atual ? (
            <div style={{ border: '1px solid #e5e0ef', borderRadius: 12, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #e5e0ef' }}>
                <b style={{ color: '#1c1530' }}>{isGestor ? nomeUsuario(atual.alunoId) : 'Tutor do curso'}</b>
                <div style={{ fontSize: 12, color: '#5b21b6' }}>{nomeCurso(atual.cursoId)}</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 440, background: '#faf9fd' }}>
                {atual.msgs.length === 0 && <div style={{ margin: 'auto', color: '#8a849c', fontSize: 13 }}>{isGestor ? 'Escreva a primeira mensagem.' : 'Escreva sua dúvida abaixo.'}</div>}
                {atual.msgs.map(m => {
                  const minha = m.autor_id === meuId;
                  return (
                    <div key={m.id} style={{ alignSelf: minha ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
                      <div style={{ background: minha ? '#7c3aed' : '#fff', color: minha ? '#fff' : '#1c1530', border: minha ? 'none' : '1px solid #e5e0ef', borderRadius: 12, padding: '8px 12px', fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {m.texto}
                      </div>
                      <div style={{ fontSize: 11, color: '#8a849c', marginTop: 2, textAlign: minha ? 'right' : 'left' }}>
                        {!minha && (m.autor_nome ? `${m.autor_nome} · ` : '')}{fmtHora(m.criado_em)}{minha && m.lida_em ? ' · lida' : ''}
                      </div>
                    </div>
                  );
                })}
                <div ref={fimRef} />
              </div>
              <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e5e0ef', alignItems: 'flex-end' }}>
                <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={2} maxLength={4000}
                  placeholder={isGestor ? 'Escreva a resposta...' : 'Escreva sua dúvida...'}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                  style={{ flex: 1, minWidth: 0, resize: 'vertical', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e0ef', fontSize: 14, fontFamily: 'inherit' }} />
                <button style={{ ...btn, background: '#7c3aed', color: '#fff', opacity: enviando || !texto.trim() ? 0.6 : 1 }} disabled={enviando || !texto.trim()} onClick={enviar}>
                  {enviando ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          ) : <div />}
        </div>
      )}
    </div>
  );
}
